import { useState, useEffect, useCallback } from 'react';
import apis from '../utils/apis';
import { getGravatarUrl } from '../utils/gravatarUtils';
import { getInitials } from '../components/shared/TailwindComponents/CollaboratorsBadge';
import { readErrorMessage } from '../utils/errorMessage';

export interface OrgMember {
  id: string;
  name: string;
  email: string;
  initials: string;
  avatarUrl: string | null;
  role?: string;
}

interface OrgPerson {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  is_active?: boolean;
  profile_picture?: string | null;
}

export const useOrgMembers = (excludeUserId?: string) => {
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apis.get('/org-people/people', {
        params: { limit: 200, page: 1 },
      });

      if (response.data?.success && response.data?.people) {
        const users: OrgPerson[] = response.data.people;

        const formattedMembers: OrgMember[] = users
          .filter(user => !excludeUserId || String(user.id) !== excludeUserId)
          .map(user => {
            const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
            const email = user.email || '';

            return {
              id: String(user.id),
              name: fullName || email.split('@')[0],
              email: email,
              initials: getInitials(fullName),
              avatarUrl: user.profile_picture || (email ? getGravatarUrl(email, 64) : null),
              role: user.role || ''
            };
          });

        setMembers(formattedMembers);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err: any) {
      const errorMsg = readErrorMessage(err, 'Failed to fetch org members');
      setError(errorMsg);
      console.error('Error fetching org members:', err);
    } finally {
      setLoading(false);
    }
  }, [excludeUserId]);

  useEffect(() => {
    fetchMembers();
  }, [excludeUserId]);

  return { members, loading, error, refetch: fetchMembers };
};
